import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useField } from 'formik';
import fontApiService from '@/dashboard/services/fonts/fontApiService';
import Select from '../Select';

const propTypes = {
  name: PropTypes.string.isRequired,
  fontFamilyFieldName: PropTypes.string.isRequired,
  label: PropTypes.string,
  transformValue: PropTypes.func,
  height: PropTypes.string,
  showAutoOption: PropTypes.bool,
  defaultValue: PropTypes.number,
};

const defaultProps = {
  label: 'Font weight',
  transformValue: value => Number(value),
  showAutoOption: true,
  defaultValue: 400,
};

// Standard weight labels mapping
const WEIGHT_LABELS = {
  100: 'Thin',
  200: 'Extra Light',
  300: 'Light',
  400: 'Regular',
  500: 'Medium',
  600: 'Semi Bold',
  700: 'Bold',
  800: 'Extra Bold',
  900: 'Black',
};

// Find the closest available weight
const findClosestWeight = (targetWeight, availableWeights, fallback = 400) => {
  if (!availableWeights || availableWeights.length === 0) return fallback;
  if (availableWeights.includes(targetWeight)) return targetWeight;

  // Find the closest weight
  return availableWeights.reduce((closest, current) => {
    const closestDiff = Math.abs(closest - targetWeight);
    const currentDiff = Math.abs(current - targetWeight);
    return currentDiff < closestDiff ? current : closest;
  });
};

export const FontWeightSelectInner = React.memo(
  ({
    name,
    fontFamily,
    value,
    onChange,
    label,
    transformValue = v => Number(v),
    height,
    isDesignForm = false,
    showAutoOption = true,
    defaultValue = 400,
  }) => {
    const [availableWeights, setAvailableWeights] = useState([
      100, 200, 300, 400, 500, 600, 700, 800, 900,
    ]);
    // Fetch available weights when font family changes
    useEffect(() => {
      // If no font or default/auto font, show all weights
      if (!fontFamily || fontFamily === '' || fontFamily === 'default') {
        setAvailableWeights([100, 200, 300, 400, 500, 600, 700, 800, 900]);
        return;
      }

      let cancelled = false;

      const fetchWeights = async () => {
        try {
          // First try to get the font data to check weights
          const fontData = await fontApiService.getFontByFamily(fontFamily);
          if (cancelled) return;
          if (fontData && fontData.weights && fontData.weights.length > 0) {
            setAvailableWeights(fontData.weights.sort((a, b) => a - b));
          } else {
            // Fallback to getFontWeights if font data doesn't include weights
            const weights = await fontApiService.getFontWeights(fontFamily);
            if (cancelled) return;
            setAvailableWeights(weights.sort((a, b) => a - b));
          }
        } catch (error) {
          console.error('Error fetching font weights:', error);
          if (cancelled) return;
          // Default to common weights on error
          setAvailableWeights([300, 400, 500, 600, 700]);
        }
      };

      fetchWeights();

      return () => {
        cancelled = true;
      };
    }, [fontFamily]);

    // Update weight when available weights change
    useEffect(() => {
      const currentWeight = value;

      // Skip if it's auto (0) and auto is allowed
      if (currentWeight === 0 && showAutoOption) return;

      // If auto is not allowed and value is 0, set to default
      if (currentWeight === 0 && !showAutoOption) {
        const closestWeight = findClosestWeight(defaultValue, availableWeights, defaultValue);
        onChange(closestWeight);
        return;
      }

      // When no specific font is selected, all weights are valid — skip the
      // availability check.  This prevents a stale availableWeights array
      // (from a previously-selected font) from overriding a just-reset value
      // before the font-fetch effect has a chance to update the weights.
      if (!fontFamily || fontFamily === '' || fontFamily === 'default') return;

      // If current weight is not available, set to closest
      if (currentWeight && !availableWeights.includes(currentWeight)) {
        const closestWeight = findClosestWeight(currentWeight, availableWeights, defaultValue);
        onChange(closestWeight);
      }
    }, [availableWeights, value, onChange, showAutoOption, defaultValue, fontFamily]);

    // Generate options with Auto (optional) + available weights
    const options = useMemo(() => {
      const weightOptions = [];

      if (showAutoOption) {
        weightOptions.push({ value: 0, label: 'Auto' });
      }

      availableWeights.forEach(weight => {
        weightOptions.push({
          value: weight,
          label: WEIGHT_LABELS[weight] || `${weight}`,
        });
      });

      return weightOptions;
    }, [availableWeights, showAutoOption]);

    return (
      <Select
        name={name}
        value={value ?? ''}
        height={height}
        isDesignForm={isDesignForm}
        options={options}
        onChange={e => {
          const newValue = transformValue ? transformValue(e.target.value) : e.target.value;
          onChange(newValue);
        }}
      />
    );
  },
  (prev, next) => {
    return (
      prev.name === next.name &&
      prev.fontFamily === next.fontFamily &&
      prev.value === next.value &&
      prev.onChange === next.onChange &&
      prev.showAutoOption === next.showAutoOption &&
      prev.defaultValue === next.defaultValue &&
      prev.isDesignForm === next.isDesignForm
    );
  },
);

FontWeightSelectInner.displayName = 'FontWeightSelectInner';

const FontWeightSelect = props => {
  const {
    name,
    fontFamilyFieldName,
    label,
    transformValue,
    height,
    isDesignForm = false,
    showAutoOption = true,
    defaultValue = 400,
  } = props;
  const [field, , helpers] = useField(name);
  const [fontFamilyField] = useField(fontFamilyFieldName);

  const fontFamily = fontFamilyField.value;

  // Keep onChange stable via ref
  const setValueRef = useRef(helpers.setValue);
  setValueRef.current = helpers.setValue;

  const handleChange = useCallback(value => setValueRef.current(value), []);

  return (
    <FontWeightSelectInner
      name={name}
      fontFamily={fontFamily}
      value={field.value}
      onChange={handleChange}
      label={label}
      transformValue={transformValue}
      height={height}
      isDesignForm={isDesignForm}
      showAutoOption={showAutoOption}
      defaultValue={defaultValue}
    />
  );
};

FontWeightSelect.propTypes = propTypes;
FontWeightSelect.defaultProps = defaultProps;

// Form field wrapper component
export const FontWeightSelectField = ({
  name,
  fontFamilyFieldName,
  label,
  transformValue,
  showAutoOption,
  defaultValue,
  ...props
}) => {
  return (
    <FontWeightSelect
      name={name}
      fontFamilyFieldName={fontFamilyFieldName}
      label={label}
      transformValue={transformValue}
      showAutoOption={showAutoOption}
      defaultValue={defaultValue}
      {...props}
    />
  );
};

export default FontWeightSelect;
